import styles from "./TransactionsAccounts.module.scss";
import {MODE_CREATE, MODE_EDIT} from "../../../codes";
import clsx from "clsx";
import {listConfigSelector, rxToggleSelectedAccount} from "../../../slices/transactionsSlice";
import React from "react";
import {useDispatch, useSelector} from "react-redux";
import {rxReorderAccounts, rxShowAccountEditor, selectAllAccounts} from "../../../slices/accountsSlice";
import useFormat from "../../../util/useFormat";
import {closestCenter, DndContext, MouseSensor, TouchSensor, useSensor, useSensors} from "@dnd-kit/core";
import {horizontalListSortingStrategy, SortableContext, useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {updatePositions} from "../../../api/ApiAccounts";
import {restrictToHorizontalAxis, restrictToParentElement} from "@dnd-kit/modifiers";
import useProject from "../../../util/useProject";
import useNetwork from "../../../util/useNetwork";
import {useTranslation} from "react-i18next";

export const TransactionsAccounts = () => {
  const dispatch = useDispatch();
  const accounts = useSelector(selectAllAccounts);

  const {t} = useTranslation("transactions");

  const {runWithExpiration} = useProject();
  const {handleNetworkError} = useNetwork();

  const {selectedAccounts, inactiveAccounts} = useSelector(listConfigSelector);

  const showAccountEditor = () => dispatch(rxShowAccountEditor({mode: MODE_CREATE, account: {}}));

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5
      }
    })
  );

  const sortAccounts = event => {
    const {active, over} = event;

    if (active.id !== over.id) {
      const activeAccount = accounts.find(a => a.id === active.id);
      const overAccount = accounts.find(a => a.id === over.id);

      const newAccounts = [];

      if (overAccount.position > activeAccount.position) {
        for (let account of accounts) {
          if (account === activeAccount) {
            continue;
          }

          newAccounts.push(account);

          if (account === overAccount) {
            newAccounts.push(activeAccount);
          }
        }
      } else {
        for (let account of accounts) {
          if (account === overAccount) {
            newAccounts.push(activeAccount);
          }

          if (account !== activeAccount) {
            newAccounts.push(account);
          }
        }
      }

      dispatch(rxReorderAccounts(newAccounts));

      updatePositions(newAccounts.map(a => a.id)).catch(handleNetworkError);
    }
  };

  return <div id={styles.accounts} className="custom-scrollbar">
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={sortAccounts}
      modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
    >
      <SortableContext
        items={accounts.filter(a => inactiveAccounts || a.active)}
        strategy={horizontalListSortingStrategy}
      >
        {accounts.filter(a => inactiveAccounts || a.active).map(a =>
          <SortableAccount key={a.id} account={a} selectedAccounts={selectedAccounts}/>)}
      </SortableContext>
    </DndContext>

    {accounts.length > 0 && <button className={styles.addAccount} onClick={runWithExpiration(showAccountEditor)}>
      <span className="material-icons">add</span>
    </button>}

    {accounts.length === 0 && <button className={clsx(styles.addAccountNoAccounts)} onClick={runWithExpiration(showAccountEditor)}>
      <span className="material-icons">add</span>

      {t("button_add_account")}
    </button>}
  </div>;
};

const SortableAccount = ({account, selectedAccounts}) => {
  const [background, color] = account.color.split("|");
  const active = selectedAccounts[account.id] || selectedAccounts[account.id] === undefined;

  const {runWithExpiration} = useProject();

  const dispatch = useDispatch();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({id: account.id});

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const {formatAmount} = useFormat();

  const toggleSelectedAccount = account => dispatch(rxToggleSelectedAccount({account}));
  const showAccountEditor = account => dispatch(rxShowAccountEditor({mode: MODE_EDIT, account}));

  return (
    <div ref={setNodeRef}
         style={style}
         {...attributes}
         {...listeners}
         className={clsx(styles.accountContainer, active ? styles.active : null)}
    >
      <div className={styles.account}>
        <div className={styles.details} style={{background}} onClick={() => toggleSelectedAccount(account)}>
          <div className={styles.name} style={{color}}>{account.name}</div>
          <div className={styles.balance} style={{color}}>{formatAmount(account.balance, account.currency)}</div>
        </div>
        <div
          className={styles.actions}
          style={{background, color}}
          onClick={runWithExpiration(() => showAccountEditor(account))}
        >
          <span className="material-icons" style={{color, opacity: 0.5}}>edit</span>

          {!account.active && <span className="material-icons" style={{color, opacity: 0.5}}>visibility_off</span>}
        </div>
      </div>

      <div className={styles.activeBar} style={{background, color}}/>
    </div>
  );
};
